export interface DiffViewProps {
  /** The earlier text — rendered in the left pane with removals struck out. */
  before: string;
  /** The later text — rendered in the right pane with additions highlighted. */
  after: string;
  /** Left-pane heading. Defaults to "Prior version". */
  beforeLabel?: string;
  /** Right-pane heading. Defaults to "Current". */
  afterLabel?: string;
  className?: string;
  dataTestId?: string;
}

type LineKind = 'eq' | 'add' | 'rem' | 'space';
interface DiffLine {
  kind: LineKind;
  text: string;
}

/**
 * LCS-based side-by-side line diff. Left pane carries removals (`rem`) +
 * shared lines (`eq`) + blank spacers (`space`) opposite additions; right pane
 * carries additions (`add`) + shared lines + spacers opposite removals. Both
 * panes end up the same length so the rows line up. O(n·m) — fine for the
 * agent-prompt bodies these editors hold.
 */
function lineDiff(beforeText: string, afterText: string): {
  left: DiffLine[];
  right: DiffLine[];
} {
  const a = (beforeText ?? '').split('\n');
  const b = (afterText ?? '').split('\n');
  const n = a.length;
  const m = b.length;
  const dp: number[][] = [];
  for (let i = 0; i <= n; i++) dp.push(new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] =
        a[i] === b[j]
          ? dp[i + 1][j + 1] + 1
          : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const left: DiffLine[] = [];
  const right: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      left.push({ kind: 'eq', text: a[i] });
      right.push({ kind: 'eq', text: b[j] });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      left.push({ kind: 'rem', text: a[i] });
      right.push({ kind: 'space', text: '' });
      i++;
    } else {
      left.push({ kind: 'space', text: '' });
      right.push({ kind: 'add', text: b[j] });
      j++;
    }
  }
  while (i < n) {
    left.push({ kind: 'rem', text: a[i++] });
    right.push({ kind: 'space', text: '' });
  }
  while (j < m) {
    left.push({ kind: 'space', text: '' });
    right.push({ kind: 'add', text: b[j++] });
  }
  return { left, right };
}

function DiffPane({
  side,
  label,
  lines
}: {
  side: 'left' | 'right';
  label: string;
  lines: DiffLine[];
}) {
  return (
    <div className={`pn-diff-pane pn-diff-pane-${side}`}>
      <h4 className="pn-diff-pane-head">{label}</h4>
      <div className="pn-diff-pane-body">
        {lines.map((ln, idx) => (
          <div
            key={idx}
            className="pn-diff-line"
            data-kind={ln.kind}
          >
            <span className="pn-diff-ln" aria-hidden="true">
              {ln.kind === 'space' ? '' : idx + 1}
            </span>
            <span className="pn-diff-text">{ln.text === '' ? ' ' : ln.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Two-pane side-by-side line diff. Red struck-out removals on the left, green
 * additions on the right, shared lines muted. Built for the settings-role-rules
 * (and settings-org-standards) version-History diff modal. CSS namespace
 * `pn-diff`; colours from `--diff-add-bg` / `--diff-rem-bg`.
 */
export function DiffView({
  before,
  after,
  beforeLabel = 'Prior version',
  afterLabel = 'Current',
  className,
  dataTestId
}: DiffViewProps) {
  const { left, right } = lineDiff(before, after);
  const cls = ['pn-diff', className].filter(Boolean).join(' ');
  return (
    <div className={cls} data-testid={dataTestId}>
      <DiffPane side="left" label={beforeLabel} lines={left} />
      <DiffPane side="right" label={afterLabel} lines={right} />
    </div>
  );
}
